import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import "../styles/StarredMessages.css";

function getPreview(item) {
  const type = item.messageType || "text";
  if (type === "image") return "Photo";
  if (type === "video") return "Video";
  if (type === "voice") return "Voice message";
  if (type === "document") return item.documentName || "Document";
  if (type === "audio") return item.documentName || "Audio";
  if (type === "contact") return "Contact";
  if (type === "location") return "Location";
  if (item.text?.trim()) return item.text.trim();
  return "Message";
}

function formatWhen(dateString) {
  const d = new Date(dateString);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function StarredMessages({ user, users = [], onBack }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    fetchStarred();
  }, [user?.id]);

  const fetchStarred = async () => {
    try {
      setLoading(true);
      setError("");
      const [directRes, groupRes] = await Promise.all([
        axios.get(`${config.apiUrl}/api/messages/starred/list/${user.id}`),
        axios.get(`${config.apiUrl}/api/groups/starred-list/${user.id}`),
      ]);
      const direct = (directRes.data.messages || []).map((m) => ({
        ...m,
        sortDate: new Date(m.createdAt).getTime(),
      }));
      const group = (groupRes.data.messages || []).map((m) => ({
        ...m,
        sortDate: new Date(m.createdAt).getTime(),
      }));
      setItems([...direct, ...group].sort((a, b) => b.sortDate - a.sortDate));
    } catch (err) {
      console.error("Error loading starred messages:", err);
      setError(err.response?.data?.error || "Failed to load starred messages");
    } finally {
      setLoading(false);
    }
  };

  const getChatTitle = (item) => {
    if (item.chatType === "group") {
      return item.groupName || "Group";
    }
    const other = users.find((u) => u.id === item.otherUserId);
    return other?.username || "Chat";
  };

  const handleOpen = (item) => {
    if (item.chatType === "group") {
      navigate(`/groups?group=${item.groupId}&message=${item.id}`);
      return;
    }
    navigate(`/chats?chat=${item.otherUserId}&message=${item.id}`);
  };

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key =
        item.chatType === "group"
          ? `group-${item.groupId}`
          : `direct-${item.otherUserId}`;
      if (!map.has(key)) {
        map.set(key, { key, title: getChatTitle(item), items: [] });
      }
      map.get(key).items.push(item);
    });
    return Array.from(map.values());
  }, [items, users]);

  return (
    <div className="starred-messages-container">
      <div className="starred-messages-header">
        <button type="button" className="back-button" onClick={onBack} aria-label="Back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Starred messages</h2>
      </div>

      <div className="starred-messages-content">
        {loading && (
          <div className="starred-messages-state">
            <div className="loading-spinner" />
            <p>Loading starred messages…</p>
          </div>
        )}

        {!loading && error && (
          <div className="starred-messages-state">
            <p>{error}</p>
            <button type="button" className="retry-button" onClick={fetchStarred}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="starred-messages-state starred-messages-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
            </svg>
            <p>No starred messages yet</p>
            <span>Star a message from any chat to see it here.</span>
          </div>
        )}

        {!loading && !error && grouped.length > 0 && (
          <div className="starred-messages-list">
            {grouped.map((section) => (
              <div key={section.key} className="starred-chat-section">
                <h3 className="starred-chat-title notranslate">{section.title}</h3>
                {section.items.map((item) => (
                  <button
                    key={`${item.chatType}-${item.id}`}
                    type="button"
                    className="starred-message-row"
                    onClick={() => handleOpen(item)}
                  >
                    <span className="starred-row-icon" aria-hidden>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1">
                        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                      </svg>
                    </span>
                    <span className="starred-row-body">
                      <span className="starred-row-preview notranslate">{getPreview(item)}</span>
                      <span className="starred-row-meta">
                        {item.isFromMe ? "You" : item.chatType === "group" ? item.senderName : "Contact"}
                        {" · "}
                        {formatWhen(item.createdAt)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default StarredMessages;
